import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { ArrowLeft, CheckCircle, ArrowRight } from 'lucide-react';
import { OptimizedImage } from '../components/ui/OptimizedImage';
import { useJsonData } from '../hooks/useJsonData';
import { useSafeAnimation } from '../components/ui/SafeMotion';

const SITE_URL = 'https://www.theecoenviro.com';

interface ServiceData {
  title: string;
  description: string;
  image: string;
  features: string[];
  process: string[];
  benefits: string[];
}

type ServicesDetailData = Record<string, ServiceData>;

const servicesList = [
  { id: 'corporate-recyclable-management', name: 'Corporate Recyclable Management' },
  { id: 'factory-waste-management', name: 'Factory Waste Management' },
  { id: 'plastic-waste-management', name: 'Plastic Waste Management' },
  { id: 'paper-dry-recyclables', name: 'Paper & Dry Recyclables' },
  { id: 'low-value-plastic-rdf', name: 'Low-Value Plastic & RDF' },
  { id: 'epr-services', name: 'EPR Services' },
  { id: 'expired-product-disposal', name: 'Expired Product Disposal' },
  { id: 'scrap-trading', name: 'Scrap Trading' },
  { id: 'ewaste-battery', name: 'E-Waste & Battery' },
];

export function ServiceDetail() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: servicesData, loading } = useJsonData<ServicesDetailData>('/data/services-detail.json');

  // SSR-safe animation props for hero section
  const leftAnim = useSafeAnimation(
    { opacity: 0, x: -30 },
    { opacity: 1, x: 0 }
  );
  const rightAnim = useSafeAnimation(
    { opacity: 0, x: 30 },
    { opacity: 1, x: 0 }
  );

  const service = serviceId && servicesData ? servicesData[serviceId] : null;

  if (loading) {
    return (
      <Layout>
        <div className="pt-32 pb-16">
          <div className="container mx-auto px-4">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-32 mb-6" />
              <div className="grid lg:grid-cols-2 gap-10">
                <div className="space-y-4">
                  <div className="h-10 bg-gray-200 rounded w-3/4" />
                  <div className="h-24 bg-gray-200 rounded" />
                  <div className="h-12 bg-gray-200 rounded w-32" />
                </div>
                <div className="h-80 bg-gray-200 rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!service) {
    return (
      <Layout>
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-2xl font-bold text-eco-green-900 mb-4">Service not found</h1>
          <Link to="/services" className="text-eco-green-600 hover:underline">
            View all services
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{service.title} | Eco Enviro - Waste Management Services India</title>
        <meta name="title" content={`${service.title} | Eco Enviro - Waste Management Services India`} />
        <meta name="description" content={service.description} />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/services/${serviceId}`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services/${serviceId}`} />
        <meta property="og:title" content={`${service.title} | Eco Enviro`} />
        <meta property="og:description" content={service.description} />
        <meta property="og:site_name" content="Eco Enviro Circular Solutions" />
        <meta property="og:image" content={`${SITE_URL}${service.image}`} />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@theecoenviro" />
        <meta name="twitter:title" content={`${service.title} | Eco Enviro`} />
        <meta name="twitter:description" content={service.description} />
        <meta name="twitter:image" content={`${SITE_URL}${service.image}`} />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-eco-green-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-eco-green-600 hover:text-eco-green-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            All Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={leftAnim.initial}
              animate={leftAnim.animate}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-3xl sm:text-4xl font-bold text-eco-green-900 mb-4">
                {service.title}
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                {service.description}
              </p>
              <Link
                to="/contact?quote=true"
                className="inline-flex items-center gap-2 px-6 py-3 bg-eco-green-600 text-white font-semibold rounded-xl hover:bg-eco-green-700 transition-colors"
              >
                Get Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={rightAnim.initial}
              animate={rightAnim.animate}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <OptimizedImage
                src={service.image}
                alt={service.title}
                className="w-full h-72 lg:h-80 object-cover rounded-2xl shadow-xl"
                priority
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-eco-green-900 mb-6">What We Offer</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-eco-green-50 p-4 rounded-xl transition-transform hover:scale-[1.02]"
              >
                <CheckCircle className="w-5 h-5 text-eco-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 bg-eco-green-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-eco-green-900 mb-6">Our Process</h2>
          <div className="flex flex-wrap gap-4">
            {service.process.map((step, index) => (
              <div
                key={index}
                className="flex items-center gap-3 bg-white px-5 py-3 rounded-xl shadow-sm transition-transform hover:scale-[1.02]"
              >
                <span className="w-8 h-8 bg-eco-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </span>
                <span className="text-gray-700">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-eco-green-900 mb-6">Benefits</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {service.benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-center gap-3 bg-eco-green-50 p-4 rounded-xl transition-transform hover:translate-x-1"
              >
                <CheckCircle className="w-5 h-5 text-eco-green-600" />
                <span className="text-gray-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-12 bg-eco-green-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-6">Other Services</h2>
          <div className="flex flex-wrap gap-3">
            {servicesList
              .filter((s) => s.id !== serviceId)
              .map((s) => (
                <Link
                  key={s.id}
                  to={`/services/${s.id}`}
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
                >
                  {s.name}
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-eco-green-900 mb-4">
            Need a Reliable Waste Management Partner?
          </h2>
          <p className="text-gray-600 mb-6">
            Get a customized waste recovery plan for your facility.
          </p>
          <Link
            to="/contact?quote=true"
            className="inline-flex items-center gap-2 px-8 py-4 bg-eco-green-600 text-white font-semibold rounded-xl hover:bg-eco-green-700 transition-colors"
          >
            Request Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
